import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import ViewToggle from "./ViewToggle";

/** Las dos formas de leer el listado de gente adentro: una fila por persona, o una por vehículo. */
export type PeopleInsideView = "person" | "vehicle";

const VIEW_OPTIONS = [
  { value: "person" as const, label: "Por persona", icon: <PersonOutlineIcon /> },
  { value: "vehicle" as const, label: "Por vehículo", icon: <LocalShippingOutlinedIcon /> },
];

const QUERY_PARAM = "view";

const isPeopleInsideView = (value: string | null): value is PeopleInsideView =>
  value === "person" || value === "vehicle";

/**
 * Vista elegida, sincronizada con `?view=` para que recargar o compartir el link la conserve.
 * Se usa `replace` y no `push`: alternar la vista no es navegación, no tiene que llenar el
 * historial ni obligar a varios "atrás" para salir de la pantalla.
 */
export const usePeopleInsideView = () => {
  const history = useHistory();
  const location = useLocation();
  const viewParam = new URLSearchParams(location.search).get(QUERY_PARAM);
  const view: PeopleInsideView = isPeopleInsideView(viewParam) ? viewParam : "person";

  const setView = (next: PeopleInsideView) => {
    const params = new URLSearchParams(location.search);
    params.set(QUERY_PARAM, next);
    history.replace({ search: params.toString() });
  };

  return { view, setView };
};

type PeopleInsideViewToggleProps = {
  value: PeopleInsideView;
  onChange: (value: PeopleInsideView) => void;
};

/** Toggle persona/vehículo, con las mismas opciones en todas las pantallas que lo muestran. */
const PeopleInsideViewToggle = ({ value, onChange }: PeopleInsideViewToggleProps) => (
  <ViewToggle
    value={value}
    onChange={onChange}
    options={VIEW_OPTIONS}
    ariaLabel="Cambiar vista del listado"
  />
);

export default PeopleInsideViewToggle;
